/**
 * BudgetForecastService
 *
 * Forecasting: where each budget is HEADED. Every figure is deterministic — computed by the
 * backend from the ledger, not by a model.
 *
 * Note the scoping: a BudgetManager may read the forecast of a budget they own or manage
 * (a manager learning their budget runs out three weeks early is the most valuable thing in
 * this feature), but the org-wide rollups are Admin/BudgetAdmin only.
 */
export class BudgetForecastService {
    constructor(http, config) {
        this.http = http;
        this.servicePath = config?.servicePath ?? BudgetForecastService.DEFAULT_SERVICE_PATH;
    }
    buildUrl(path) {
        const url = `${this.servicePath}/api/${path}`;
        return url.startsWith('/') ? url : '/' + url;
    }
    /** One budget's forecast: series, projection, risk, confidence. */
    async getForecast(budgetId) {
        const response = await this.getForecastRaw(budgetId);
        return response.data ?? null;
    }
    getForecastRaw(budgetId) {
        return this.http.get(this.buildUrl(`budgets/${budgetId}/forecast`));
    }
    /**
     * How the forecast itself has MOVED — the nightly snapshot history. A projected overrun
     * that has doubled in a fortnight is a different story from one that has been stable.
     */
    async getHistory(budgetId) {
        const response = await this.getHistoryRaw(budgetId);
        return response.data ?? [];
    }
    getHistoryRaw(budgetId) {
        return this.http.get(this.buildUrl(`budgets/${budgetId}/forecast/history`));
    }
    /** The org's forward position: at-risk budgets, projected overrun, runway ranking. */
    async getOverview() {
        const response = await this.getOverviewRaw();
        return response.data ?? null;
    }
    getOverviewRaw() {
        return this.http.get(this.buildUrl('budget-dashboard/forecast'));
    }
    /** Monthly spend over time. budgetId scopes it; from/to are yyyyMMddHHmmss stamps. */
    async getTrends(params) {
        const response = await this.getTrendsRaw(params);
        return response.data ?? null;
    }
    getTrendsRaw(params) {
        const query = new URLSearchParams();
        if (params?.budgetId != null)
            query.set('budgetId', String(params.budgetId));
        if (params?.from != null)
            query.set('from', String(params.from));
        if (params?.to != null)
            query.set('to', String(params.to));
        const qs = query.toString();
        return this.http.get(this.buildUrl(`budget-dashboard/trends${qs ? `?${qs}` : ''}`));
    }
    /** Plan vs actual for one budget, by category or allocation. */
    async getVariance(budgetId, dimension = 'category') {
        const response = await this.getVarianceRaw(budgetId, dimension);
        return response.data ?? null;
    }
    getVarianceRaw(budgetId, dimension = 'category') {
        return this.http.get(this.buildUrl(`budget-dashboard/variance?budgetId=${budgetId}&dimension=${dimension}`));
    }
    /** Released vs spent by month across EVERY budget — the org cashflow view. */
    async getCashflow(params) {
        const response = await this.getCashflowRaw(params);
        return response.data ?? null;
    }
    getCashflowRaw(params) {
        const query = new URLSearchParams();
        if (params?.from != null)
            query.set('from', String(params.from));
        if (params?.to != null)
            query.set('to', String(params.to));
        const qs = query.toString();
        return this.http.get(this.buildUrl(`budget-dashboard/cashflow${qs ? `?${qs}` : ''}`));
    }
}
BudgetForecastService.DEFAULT_SERVICE_PATH = 'budgets';
//# sourceMappingURL=BudgetForecastService.js.map